/**
 * WsSession — owns the WebSocket protocol lifecycle (protocol.md §4/§6, web-client.md §2):
 *
 *   connect → sys.hello(token, versions, platform, language, resume[]) → sys.welcome (apply flags,
 *   resume plan) → replay evt.* in seq order → sys.live → LIVE.
 *
 * Responsibilities: ack table keyed by cmdId with 10 s same-cmdId retry; heartbeat (sys.ping/pong
 * ~20 s) + silence watchdog; sys.reauth before token expiry; sys.drain → immediate reconnect;
 * exponential backoff with full jitter (base 1 s ×2, cap 30 s), reset after 60 s healthy; gap →
 * resume; stateHash drift → resync. The UI never constructs envelopes — it calls {@link send}.
 */
import {
  ProtocolError,
  PROTOCOL_VERSION,
  WireTypes,
  command,
  control,
  decode,
  encode,
  uuidv7,
} from '../protocol/index.ts';
import type {
  AckPayload,
  ApiError,
  DrainPayload,
  Envelope,
  GameActionPayload,
  GameStatePayload,
  HelloPayload,
  LivePayload,
  NoticePayload,
  ReauthPayload,
  WelcomePayload,
} from '../protocol/index.ts';
import { ErrorCodes } from '../protocol/index.ts';
import type { RetroDocument } from '../rest/index.ts';
import type { Store } from '../state/index.ts';
import { backoffDelay, DEFAULT_BACKOFF } from './backoff.ts';
import type { BackoffConfig } from './backoff.ts';
import { systemTimers } from './timers.ts';
import type { Timers, TimerHandle } from './timers.ts';
import type { Transport, TransportFactory } from './transport.ts';

export interface WsSessionConfig {
  pingIntervalMs: number;
  ackTimeoutMs: number;
  /** Any silence longer than this is treated as a dead connection (protocol.md §4.5). */
  silenceTimeoutMs: number;
  /** Backoff attempt counter resets after this long on a healthy connection (protocol.md §4.8). */
  healthyResetMs: number;
  /** Handshake budget: no sys.welcome within this window → drop and reconnect. */
  handshakeTimeoutMs: number;
  backoff: BackoffConfig;
}

export const DEFAULT_WS_CONFIG: WsSessionConfig = {
  pingIntervalMs: 20_000,
  ackTimeoutMs: 10_000,
  silenceTimeoutMs: 45_000,
  healthyResetMs: 60_000,
  handshakeTimeoutMs: 10_000,
  backoff: DEFAULT_BACKOFF,
};

export interface WsSessionOptions {
  url: string;
  transportFactory: TransportFactory;
  store: Store;
  /** Current in-memory access token (security §2.1). Called fresh on each (re)connect. */
  tokenProvider: () => string | null;
  clientVersion: string;
  platform: string;
  language: string;
  timers?: Timers;
  config?: Partial<WsSessionConfig>;
  /** Fired when the server rejects auth (AUTH_EXPIRED/AUTH_INVALID); host should refresh + reconnect. */
  onAuthError?: (error: ApiError) => void;
  /** Fired on UPGRADE_REQUIRED — the client must block and update (protocol.md §7.3). */
  onUpgradeRequired?: (error: ApiError) => void;
  onNotice?: (notice: NoticePayload) => void;
  /**
   * Optional observer of the command lifecycle (cmdId/type/phase), fed to the diagnostics command
   * log. Structurally matches {@link CommandRecord}; carries NO payload/token (web-client.md §8).
   */
  onCommand?: (record: {
    cmdId: string;
    type: string;
    phase: 'sent' | 'accepted' | 'rejected';
    at: number;
  }) => void;
}

interface Pending {
  envelope: Envelope;
  resolve: (ack: AckPayload) => void;
  reject: (error: ProtocolError) => void;
  retry: TimerHandle | null;
}

type Phase = 'idle' | 'connecting' | 'handshaking' | 'live' | 'reconnecting' | 'closed';

export class WsSession {
  private readonly o: WsSessionOptions;
  private readonly t: Timers;
  private readonly cfg: WsSessionConfig;

  private transport: Transport | null = null;
  private phase: Phase = 'idle';
  private intentionalClose = false;
  private attempt = 0;
  private lastWelcomeAt = 0;

  private readonly pending = new Map<string, Pending>();

  private heartbeat: TimerHandle | null = null;
  private watchdog: TimerHandle | null = null;
  private handshakeTimer: TimerHandle | null = null;
  private reconnectTimer: TimerHandle | null = null;

  constructor(options: WsSessionOptions) {
    this.o = options;
    this.t = options.timers ?? systemTimers;
    this.cfg = { ...DEFAULT_WS_CONFIG, ...options.config };
  }

  // ---- public API -------------------------------------------------------------------------------

  /** Open the connection (idempotent while already connecting/live). */
  start(): void {
    if (this.phase === 'connecting' || this.phase === 'handshaking' || this.phase === 'live') {
      return;
    }
    this.intentionalClose = false;
    this.connect();
  }

  /** Permanently close: cancel timers, reject in-flight commands, do not reconnect. */
  stop(reason = 'client stop'): void {
    this.intentionalClose = true;
    this.clearReconnect();
    this.teardownConnection();
    this.rejectAllPending(
      new ProtocolError({ code: 'CLIENT_CLOSED', message: reason, traceId: null }),
    );
    this.setPhase('closed');
  }

  /**
   * Send a command. Assigns a UUIDv7 cmdId, tracks it in the ack table, and resolves on sys.ack
   * (accepted) or rejects with the canonical error (rejected). Retries the SAME cmdId every
   * ackTimeoutMs until acked or the connection is torn down (protocol.md §6 idempotency).
   */
  send<P>(type: string, session: string, payload: P, cmdId = this.newCmdId()): Promise<AckPayload> {
    const envelope = command(type, session, cmdId, payload);
    return new Promise<AckPayload>((resolve, reject) => {
      const entry: Pending = { envelope, resolve, reject, retry: null };
      this.pending.set(cmdId, entry);
      this.o.onCommand?.({ cmdId, type, phase: 'sent', at: this.t.now() });
      this.transmit(envelope);
      entry.retry = this.armRetry(cmdId);
    });
  }

  /**
   * A command id, minted the way {@link send} mints them.
   *
   * Exposed because the OUTBOX needs the id at the moment the gesture is made, not when the promise
   * resolves: a line drawn optimistically has to be keyed by something, and its ack has to be able to
   * find it again. Minted here rather than in the caller so it keeps using this session's injected
   * clock, which is what keeps the ids ordered and the tests deterministic.
   */
  newCmdId(): string {
    return uuidv7(this.t.now());
  }

  /**
   * Convenience: send a chat line (protocol.md §5.1, cmd.chat.send).
   *
   * `channel` is the room it was typed in (register item 16). Omitted rather than sent as undefined
   * when there is none, so a client that does not model rooms puts exactly the frame it always did
   * on the wire.
   */
  sendChat(session: string, text: string, channel?: string, cmdId?: string): Promise<AckPayload> {
    return this.send(WireTypes.ChatSend, session, channel ? { text, channel } : { text }, cmdId);
  }

  /**
   * React to a message by its committed seq (workspace-client.md §2i, cmd.chat.react).
   *
   * Expressive and cheap: the signal is committed so an NPC's memory fold can cite it ("you reacted
   * and never replied"), and it moves no meter - the moment an emoji is worth morale it stops being
   * expression and becomes a button to farm.
   */
  sendReaction(
    session: string,
    seq: number,
    reaction: string,
    cmdId?: string,
  ): Promise<AckPayload> {
    return this.send(WireTypes.ChatReact, session, { seq, reaction }, cmdId);
  }

  /** Send a gameplay input to the session's engine (the S1 bridge, cmd.game.action). */
  sendGameAction(session: string, input: GameActionPayload): Promise<AckPayload> {
    return this.send(WireTypes.GameAction, session, input);
  }

  /** Rotate the access token on the live socket before expiry (protocol.md §4.6). */
  reauth(token: string): void {
    const payload: ReauthPayload = { token };
    this.transmit(control(WireTypes.Reauth, payload));
  }

  isLive(): boolean {
    return this.phase === 'live';
  }

  /**
   * Force an immediate resume-reconnect. Used after a REST join/create so the next `sys.welcome`
   * re-plans the resume set and subscribes to the newly-joined session (the backend subscribes to
   * every membership at welcome time, ResumeService.PlanAsync).
   */
  reconnect(): void {
    if (this.phase === 'idle' || this.phase === 'closed') {
      this.start();
      return;
    }
    this.reconnectNow();
  }

  // ---- connection lifecycle ---------------------------------------------------------------------

  private connect(): void {
    this.setPhase('connecting');
    const transport = this.o.transportFactory(this.o.url);
    this.transport = transport;

    transport.onOpen(() => this.onOpen());
    transport.onMessage((data) => this.onMessage(data));
    transport.onClose(() => this.onClose());
    transport.onError(() => {
      /* errors surface as a close; nothing actionable here */
    });
  }

  private onOpen(): void {
    const token = this.o.tokenProvider();
    if (!token) {
      // No credential to present — surface as an auth error and let the host refresh.
      this.o.onAuthError?.({
        code: ErrorCodes.AuthInvalid,
        message: 'No access token',
        traceId: null,
      });
      this.teardownConnection();
      this.scheduleReconnect();
      return;
    }
    this.setPhase('handshaking');
    const hello: HelloPayload = {
      token,
      protocolVersion: PROTOCOL_VERSION,
      clientVersion: this.o.clientVersion,
      platform: this.o.platform,
      clientType: 'web',
      language: this.o.language,
      resume: this.o.store.resumeEntries(),
    };
    this.transmit(control(WireTypes.Hello, hello));
    this.handshakeTimer = this.t.setTimeout(() => {
      // No welcome in time — treat as a failed attempt and back off.
      this.teardownConnection();
      this.scheduleReconnect();
    }, this.cfg.handshakeTimeoutMs);
  }

  private onClose(): void {
    this.stopHeartbeat();
    this.clearTimer('handshakeTimer');
    if (this.intentionalClose) {
      return;
    }
    this.scheduleReconnect();
  }

  private scheduleReconnect(): void {
    if (this.intentionalClose || this.reconnectTimer) {
      return;
    }
    // Reset the attempt counter after a spell of healthy connection (protocol.md §4.8).
    if (this.lastWelcomeAt && this.t.now() - this.lastWelcomeAt > this.cfg.healthyResetMs) {
      this.attempt = 0;
    }
    this.attempt += 1;
    const delay = backoffDelay(this.attempt, this.cfg.backoff, () => this.t.random());
    this.setPhase('reconnecting');
    this.o.store.setConnection({ attempt: this.attempt });
    this.reconnectTimer = this.t.setTimeout(() => {
      this.reconnectTimer = null;
      this.connect();
    }, delay);
  }

  /** Immediate, backoff-free reconnect (protocol.md §4.7 drain, §4.3 resume-on-gap). */
  private reconnectNow(): void {
    if (this.intentionalClose) return;
    this.teardownConnection();
    this.clearReconnect();
    this.setPhase('reconnecting');
    this.connect();
  }

  // ---- inbound routing --------------------------------------------------------------------------

  private onMessage(raw: string): void {
    this.resetWatchdog();
    const env = decode(raw);
    if (!env) return; // tolerant reader: drop garbage.

    switch (env.type) {
      case WireTypes.Welcome:
        this.onWelcome(env as Envelope<WelcomePayload>);
        break;
      case WireTypes.Live:
        this.onLiveFrame(env as Envelope<LivePayload>);
        break;
      case WireTypes.Ack:
        this.onAck(env as Envelope<AckPayload>);
        break;
      case WireTypes.Error:
        this.onSysError(env as Envelope<ApiError>);
        break;
      case WireTypes.Ping:
        this.transmit(control(WireTypes.Pong, {}));
        break;
      case WireTypes.Pong:
        break;
      case WireTypes.Drain:
        this.onDrain(env as Envelope<DrainPayload>);
        break;
      case WireTypes.Notice:
        this.o.onNotice?.(env.payload as NoticePayload);
        break;
      case WireTypes.StateDelta:
        // The S1 engine bridge: an ephemeral per-seat-projected state delta (unsequenced, latest wins).
        if (env.session) this.o.store.applyStateDelta(env.session, env.payload as GameStatePayload);
        break;
      case WireTypes.Retro:
        // The team-wide retro package, broadcast when the session ends.
        if (env.session) this.o.store.applyRetro(env.session, env.payload as RetroDocument);
        break;
      // The streaming sub-protocol (protocol.md §8): ephemeral, unsequenced, never resumed. A reconnect
      // mid-stream misses the rest and renders the committed event instead, so none of this gates on seq.
      case WireTypes.NpcTyping: {
        const p = env.payload as { seat?: string; typing?: boolean };
        if (env.session && typeof p?.seat === 'string') {
          this.o.store.applyTyping(env.session, p.seat, p.typing === true);
        }
        return;
      }
      case WireTypes.StreamOpen: {
        const p = env.payload as { streamId: string; kind: string };
        if (env.session) this.o.store.applyStreamOpen(env.session, p.streamId, p.kind);
        break;
      }
      case WireTypes.StreamChunk: {
        const p = env.payload as { streamId: string; payload?: { text?: string } };
        if (env.session)
          this.o.store.applyStreamChunk(env.session, p.streamId, p.payload?.text ?? '');
        break;
      }
      case WireTypes.StreamEnd: {
        const p = env.payload as { streamId: string; status: string };
        if (env.session) this.o.store.applyStreamEnd(env.session, p.streamId, p.status);
        break;
      }

      default:
        if (env.type.startsWith('evt.')) {
          this.onEvent(env);
        }
        break;
    }
  }

  private onWelcome(env: Envelope<WelcomePayload>): void {
    this.clearTimer('handshakeTimer');
    this.setPhase('live');
    this.lastWelcomeAt = this.t.now();
    this.o.store.applyWelcome(env.payload);
    this.o.store.setConnection({ status: 'live', attempt: 0, lastError: null });
    this.startHeartbeat();
    this.resetWatchdog();
    // Re-drive any commands that were in flight when the socket dropped (same cmdId → idempotent).
    for (const [cmdId, entry] of this.pending) {
      this.transmit(entry.envelope);
      this.rearmRetry(cmdId, entry);
    }
  }

  private onLiveFrame(env: Envelope<LivePayload>): void {
    const p = env.payload;
    if (p?.session) this.o.store.markLive(p.session, p.seq ?? 0);
  }

  private onEvent(env: Envelope): void {
    const result = this.o.store.applyEvent(env);
    if (result === 'gap') {
      // Missing events: resume by reconnecting with our lastSeq (protocol.md §6, no guessing).
      this.reconnectNow();
    } else if (result === 'drift') {
      this.resync(env.session ?? null);
    }
  }

  private onAck(env: Envelope<AckPayload>): void {
    const ack = env.payload;
    const cmdId = ack?.cmdId ?? env.cmdId;
    if (!cmdId) return;
    const entry = this.pending.get(cmdId);
    if (!entry) return; // already resolved (a duplicate ack) — ignore.
    this.clearPending(cmdId);
    this.o.onCommand?.({
      cmdId,
      type: entry.envelope.type,
      phase: ack.status === 'accepted' ? 'accepted' : 'rejected',
      at: this.t.now(),
    });
    if (ack.status === 'accepted') {
      entry.resolve(ack);
    } else {
      const error: ApiError = ack.error ?? {
        code: ErrorCodes.ValidationFailed,
        message: 'Command rejected',
        traceId: null,
      };
      entry.reject(new ProtocolError(error));
    }
  }

  private onSysError(env: Envelope<ApiError>): void {
    const error = env.payload;
    this.o.store.setConnection({ lastError: error.code });
    if (error.code === ErrorCodes.UpgradeRequired) {
      this.intentionalClose = true;
      this.setPhase('closed');
      this.o.store.setConnection({ status: 'upgrade-required' });
      this.o.onUpgradeRequired?.(error);
      this.teardownConnection();
      return;
    }
    if (error.code === ErrorCodes.AuthExpired || error.code === ErrorCodes.AuthInvalid) {
      this.o.onAuthError?.(error);
      // The server closes after a handshake auth error; the close handler drives reconnect once
      // the host has refreshed the token.
    }
  }

  private onDrain(env: Envelope<DrainPayload>): void {
    void env;
    // Rolling deploy: reconnect immediately to a live instance and resume (protocol.md §4.7).
    this.reconnectNow();
  }

  // ---- drift resync -----------------------------------------------------------------------------

  private resync(session: string | null): void {
    if (!session) return;
    // Preferred: ask the server for a fresh snapshot (protocol.md §7). The S0 backend does not yet
    // dispatch cmd.session.sync, so a rejection falls back to a full resume-reconnect.
    this.send(WireTypes.SessionSync, session, {}).catch(() => this.reconnectNow());
  }

  // ---- ack retry --------------------------------------------------------------------------------

  private armRetry(cmdId: string): TimerHandle {
    return this.t.setTimeout(() => {
      const entry = this.pending.get(cmdId);
      if (!entry) return;
      this.transmit(entry.envelope); // resend SAME cmdId (idempotent, protocol.md §6).
      this.rearmRetry(cmdId, entry);
    }, this.cfg.ackTimeoutMs);
  }

  private rearmRetry(cmdId: string, entry: Pending): void {
    this.t.clearTimeout(entry.retry);
    entry.retry = this.armRetry(cmdId);
  }

  private clearPending(cmdId: string): void {
    const entry = this.pending.get(cmdId);
    if (entry) this.t.clearTimeout(entry.retry);
    this.pending.delete(cmdId);
  }

  private rejectAllPending(error: ProtocolError): void {
    for (const [cmdId, entry] of this.pending) {
      this.t.clearTimeout(entry.retry);
      entry.reject(error);
      this.pending.delete(cmdId);
    }
  }

  // ---- heartbeat / watchdog ---------------------------------------------------------------------

  private startHeartbeat(): void {
    this.stopHeartbeat();
    this.heartbeat = this.t.setInterval(() => {
      this.transmit(control(WireTypes.Ping, {}));
    }, this.cfg.pingIntervalMs);
  }

  private stopHeartbeat(): void {
    this.t.clearInterval(this.heartbeat);
    this.heartbeat = null;
  }

  private resetWatchdog(): void {
    this.t.clearTimeout(this.watchdog);
    this.watchdog = this.t.setTimeout(() => {
      // Silence beyond the threshold → assume dead and reconnect (protocol.md §4.5).
      this.reconnectNow();
    }, this.cfg.silenceTimeoutMs);
  }

  // ---- helpers ----------------------------------------------------------------------------------

  private transmit(env: Envelope): void {
    this.transport?.send(encode(env));
  }

  private teardownConnection(): void {
    this.stopHeartbeat();
    this.t.clearTimeout(this.watchdog);
    this.watchdog = null;
    this.clearTimer('handshakeTimer');
    if (this.transport) {
      this.transport.close(1000, 'client');
      this.transport = null;
    }
  }

  private clearReconnect(): void {
    if (this.reconnectTimer) {
      this.t.clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
  }

  private clearTimer(name: 'handshakeTimer'): void {
    if (this[name]) {
      this.t.clearTimeout(this[name]);
      this[name] = null;
    }
  }

  private setPhase(phase: Phase): void {
    this.phase = phase;
    const status =
      phase === 'live'
        ? 'live'
        : phase === 'reconnecting'
          ? 'reconnecting'
          : phase === 'closed'
            ? 'closed'
            : phase === 'idle'
              ? 'idle'
              : phase === 'connecting'
                ? 'connecting'
                : 'handshaking';
    this.o.store.setConnection({ status });
  }
}
